import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { ExecutiveCoverageResponse, ExecutiveRefreshResponse } from '../../shared/api-types.js';
import { api } from '../api.js';
import { PATHS } from '../paths.js';
import { Banner, Page, PageHead, Section, StateView, StatusPill, formatCount, formatDuration, formatPercent, pluralise, useNow, useRequest } from '../ui/index.js';
import { AuthorLine, ExecutiveFailure, ExecutiveStrip, RefreshNote, RefreshNow } from './executive-parts.js';
import {
  SLACK_CLIMATE_ON,
  backfillFraction,
  backfillProgressSentence,
  coverageSlackText,
  firstReportWaitSentence,
  nextScheduledText,
  resummaryStage,
  runningSentence,
  runningWord,
  utcDateTime,
  withheldReasonText,
} from './lib/executive.js';
import { ReportStatePill } from './executive-report.js';

// The Coverage section (ADR 0019; design §2.13 "Coverage"; 06-modules §6's questions, answered on the screen without a
// log): who the report reads and how many, what it read last, when it last succeeded and how long it took, whether one is
// being written now, when the next is due, the floors it runs under (no minimum since the owner's ruling of 2026-10-12,
// "Remove them everywhere"; the route serves none), how long reports are kept, how many
// executives may read it, and how far the one-off re-summary has come — with the first report's wait while it lasts (the
// owner's rulings of 2026-10-08, "Wait for the backfill", "Wait for the next report hour", "Day counts from first due
// report"). Counts only, never a name. Declared on the README's Modules line only after the ruled /conformity audit (E16).

const RUNNING_POLL_MS = 15_000;

export function CoverageSection() {
  const request = useRequest<ExecutiveCoverageResponse>((signal) => api.get<ExecutiveCoverageResponse>('/api/executive/coverage', { signal }), []);
  const data = request.data;
  const reload = request.reload;
  const failed = request.status === 'error' && request.error !== null;
  // While a report is written, or the first report waits, the figures move: they are read again until they settle.
  useEffect(() => {
    if (failed || !data || (!data.running && data.backfill.state !== 'running')) return;
    const timer = window.setTimeout(reload, RUNNING_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [data, failed, reload]);
  const onAnswered = (_answer: ExecutiveRefreshResponse): void => reload();
  return (
    <Page measure="read-wide" className="executive-coverage">
      <PageHead
        title="Coverage"
        subtitle="What the organisation report reads, when it last ran, and what holds it back."
        // Why the cooldown (the owner's ruling of 2026-10-09 on ADR 0019's Open 39, "Coverage knows the cooldown"): Coverage's route carries it, so the
        // button says when it may next be pressed rather than appearing pressable and being refused (06-modules §6.6).
        actions={data && !failed ? <RefreshNow cooldownUntil={data.cooldownUntil} running={data.running !== null} onAnswered={onAnswered} /> : undefined}
        strip={<ExecutiveStrip />}
      />
      {request.status === 'loading' && !data ? <StateView kind="loading" label="Loading coverage" minHeight="24rem" /> : null}
      {/* Why a failure is shown whenever the current read failed, figures or not (the stage 6 review; 03-components §1): the
          figures are read again while they move, and a look that failed must not leave the last ones standing as current. */}
      {failed && request.error ? <ExecutiveFailure failure={request.error} what="coverage" onRetry={reload} /> : null}
      {data && !failed ? <CoverageBody data={data} /> : null}
      <AuthorLine />
    </Page>
  );
}

function CoverageBody({ data }: { data: ExecutiveCoverageResponse }) {
  const now = useNow();
  const fraction = backfillFraction(data.backfill);
  const last = data.lastReport;
  // Why the stage from the counts first (lib/executive.ts `resummaryStage`): the wait counts the re-summary finished once
  // none is to go or being re-summarised, whatever the state word, which says Inactive first while it is held back.
  const stage = resummaryStage(data.backfill, data.backfill.state === 'paused');
  return (
    <>
      <RefreshNote cooldownUntil={data.cooldownUntil} now={now} />
      {data.firstReport.state === 'waiting' ? (
        <Banner tone="info" icon="clock" title="The first report is waiting">{firstReportWaitSentence(data.firstReport.until, stage, now)}</Banner>
      ) : null}
      {data.running ? <Banner tone="info" icon="clock" title={runningWord(data.running.status)}>{runningSentence(data.running, now)}</Banner> : null}

      <Section id="report-run" title="The report">
        <dl className="field-list">
          <dt>Last report</dt>
          <dd>
            {last ? (
              <span className="cluster">
                <Link to={PATHS.executiveReport(last.id)} className="hit-area">{`Up to ${utcDateTime(last.windowEnd)}`}</Link>
                <ReportStatePill status={last.status} />
                {/* Why the reason beside a withheld report's pill (the owner's ruling of 2026-10-13 on ADR 0019's Open 50, "Record
                    the reasons"): Coverage states the cause its executor recorded, as the report's own record does. */}
                {last.status === 'insufficient' ? <span className="text-sm secondary">{withheldReasonText({ people: last.people, meetings: last.meetings, reason: last.withheldReason })}</span> : null}
              </span>
            ) : 'None yet'}
          </dd>
          <dt>Last success</dt>
          <dd>
            {data.lastSuccess ? (
              <>
                {/* Why whole alternatives (05-content §8; the Phase 1c audit's D30): ", written in …" was spliced on by condition. */}
                {data.lastSuccess.durationMs !== null
                  ? `${utcDateTime(data.lastSuccess.at)}, written in ${formatDuration(data.lastSuccess.durationMs / 1000)}`
                  : utcDateTime(data.lastSuccess.at)}
              </>
            ) : '— never'}
          </dd>
          <dt>Running now</dt>
          <dd>{data.running ? <StatusPill tone="info" icon="clock">{runningWord(data.running.status)}</StatusPill> : 'No'}</dd>
          <dt>Next scheduled report</dt>
          <dd>{nextScheduledText(data, stage, now)}</dd>
          <dt>Schedule</dt>
          <dd>{data.cadence === 'weekly' ? `Weekly, at ${String(data.hourUtc).padStart(2, '0')}:00 UTC` : `Daily, at ${String(data.hourUtc).padStart(2, '0')}:00 UTC`}</dd>
        </dl>
      </Section>

      <Section id="report-reach" title="Who it reads" description="Counts from the last report; never a name.">
        {last ? (
          <dl className="field-list">
            <dt>Members</dt>
            <dd className="num">{formatCount(last.membersTotal)}</dd>
            {/* One label since the owner's ruling of 2026-10-12: no minimum is counted for. */}
            <dt>Contributors in the last report</dt>
            <dd className="num">{formatCount(last.people)}</dd>
            <dt>Members without AI summaries</dt>
            <dd className="num">{formatCount(last.withoutGrant)}</dd>
            <dt>Meetings read</dt>
            <dd className="num">{formatCount(last.meetings)}</dd>
            <dt>Slack</dt>
            {/* Why the reason in words: the reason the report recorded as it was written, as the report's own basis says it
                (Open 45; a withheld report's too since the owner's ruling of 2026-10-13 on Open 54, "Record the reasons"); for a
                report that recorded none, the floor read now. */}
            <dd>{coverageSlackText(last, data.floors.slackClimate)}</dd>
          </dl>
        ) : (
          <p className="secondary">Nothing is read until the first report is written.</p>
        )}
      </Section>

      <Section id="report-rules" title="What it runs under">
        <dl className="field-list">
          {/* Why no Minimum row (the owner's ruling of 2026-10-12, "Remove them everywhere"): no minimum applies, and the
              route serves none. */}
          <dt>Shared project names</dt>
          {/* Why "where at least three people's meetings stand behind it" (the owner's ruling of 2026-10-12, "Names only where
              three"): the one three-person rule left, permission model 1.3.0 §9.1 condition 5 — said where the report's rules are. */}
          <dd>{data.floors.projects === 'on' ? 'On — a project two or more people share may be named, where at least three people’s meetings stand behind it' : 'Off — no project is named'}</dd>
          <dt>Slack climate</dt>
          {/* Why every member connected to Slack with both Slack permissions, "whether or not Calis makes their summaries" (the
              owner's ruling of 2026-10-13 on ADR 0019's Open 56, "Count them too"), where it said "every connected member whose
              summaries Calis makes": the report now reads the Slack marks of every admitted member who passes the Slack gate
              with a live connection, AI summaries or not. The sentence says whose it counts (lib/executive.ts SLACK_CLIMATE_ON). */}
          <dd>{data.floors.slackClimate === 'on' ? SLACK_CLIMATE_ON : 'Off on this server'}</dd>
          <dt>Kept for</dt>
          <dd>{pluralise(data.retentionDays, { one: '# day', other: '# days' })}</dd>
          <dt>Executives who can read it</dt>
          <dd className="num">{formatCount(data.executives)}</dd>
        </dl>
      </Section>

      <Section id="resummary" title="The one-off re-summary" description="Once, each member’s recent meetings are summarised again so each carries a climate word.">
        <dl className="field-list">
          <dt>State</dt>
          <dd>
            {data.backfill.state === 'done' ? <StatusPill tone="success">Completed</StatusPill> : data.backfill.state === 'paused' ? <StatusPill tone="neutral" icon="minus">Inactive</StatusPill> : <StatusPill tone="info" icon="clock">Running</StatusPill>}
            {data.backfill.state === 'paused' ? <span className="text-sm secondary"> Held back on this server by its operator</span> : null}
          </dd>
          <dt>Progress</dt>
          <dd>{backfillProgressSentence(data.backfill)}</dd>
        </dl>
        {fraction !== null ? (
          <div className="executive-progress">
            <progress max={1} value={fraction} aria-label="Members re-summarised" />
            <span className="text-sm num">{formatPercent(fraction)}</span>
          </div>
        ) : null}
      </Section>
    </>
  );
}
